import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { PageHeader } from "@/components/app/page-header";
import { HelpPanel } from "@/components/app/help-panel";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Plus,
  Copy,
  Code2,
  Megaphone,
  Mail,
  Download,
  Trash2,
  PlayCircle,
  Loader2,
  CheckCircle2,
  Clock3,
  AlertCircle,
  Play,
  Pause,
  Square,
} from "lucide-react";
import { Wand2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { validateTrackingSnippet } from "@/lib/snippet-validation";
import { minimizePixelSnippet } from "@/lib/pixel-snippet";
import type { SnippetValidationResult } from "@/lib/snippet-validation";
import {
  STEALTH_STYLES,
  type StealthStyle,
  listSnippetVariants,
  createSnippetVariant,
  deleteSnippetVariant,
  getVariantStats,
} from "@/lib/snippet-variants.functions";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useServerFn } from "@tanstack/react-start";
import { getOrCreateCampaignAlias } from "@/lib/campaign-alias.functions";
import { hitPixelTestEndpoints, verifyPixelTestEvent } from "@/lib/pixel-test.functions";
import {
  sendCampaignE2ETestEmail,
  verifyCampaignE2EOpen,
  listCampaignTestRecipients,
  previewCampaignE2ETestEmail,
} from "@/lib/campaign-e2e-test.functions";
import { Send, Zap, Eye } from "lucide-react";

export const Route = createFileRoute("/_authenticated/campaigns")({
  component: CampaignsPage,
  head: () => ({ meta: [{ title: "Campaigns — InboxPulse" }] }),
});

function CampaignsPage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  // Tick alle 1s, damit Pause-Countdown live runter zählt
  const [, setNowTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setNowTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, []);
  const { data } = useQuery({
    queryKey: ["campaigns"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("campaigns")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
    refetchInterval: (q) => {
      const rows = (q.state.data ?? []) as Array<{ status?: string }>;
      return rows.some((r) => r.status === "running") ? 3000 : false;
    },
  });
  const { data: senders } = useQuery({
    queryKey: ["senders-for-pixel"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("senders")
        .select("id, email, display_name")
        .order("email");
      if (error) throw error;
      return data ?? [];
    },
  });
  const { data: recipientCounts } = useQuery({
    queryKey: [
      "campaign-recipient-counts",
      (data ?? []).map((c: { id: string }) => c.id).join(","),
    ],
    enabled: !!data && data.length > 0,
    queryFn: async () => {
      const ids = (data ?? []).map((c: { id: string }) => c.id);
      const counts: Record<string, number> = {};
      await Promise.all(
        ids.map(async (id) => {
          const { count, error } = await supabase
            .from("campaign_recipients")
            .select("id", { count: "exact", head: true })
            .eq("campaign_id", id);
          if (error) throw error;
          counts[id] = count ?? 0;
        }),
      );
      return counts;
    },
  });
  void senders;

  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [replyTo, setReplyTo] = useState("");

  const create = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.from("campaigns").insert({
        name,
        description: desc,
        reply_to: replyTo.trim().toLowerCase() || null,
        user_id: user!.id,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Campaign created");
      setOpen(false);
      setName("");
      setDesc("");
      setReplyTo("");
      qc.invalidateQueries({ queryKey: ["campaigns"] });
    },
    onError: (e: any) => toast.error(e.message),
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("campaigns").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Kampagne gelöscht");
      qc.invalidateQueries({ queryKey: ["campaigns"] });
    },
    onError: (e: any) => toast.error(e.message),
  });

  const setStatus = useMutation({
    mutationFn: async ({
      id,
      status,
    }: {
      id: string;
      status: "running" | "paused" | "stopped";
    }) => {
      const { error } = await supabase.from("campaigns").update({ status }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: (_d, vars) => {
      const label =
        vars.status === "running"
          ? "gestartet"
          : vars.status === "paused"
            ? "pausiert"
            : "gestoppt";
      toast.success(`Kampagne ${label}`);
      qc.invalidateQueries({ queryKey: ["campaigns"] });
    },
    onError: (e: any) => toast.error(e.message),
  });

  const setBounceTg = useMutation({
    mutationFn: async ({ id, value }: { id: string; value: boolean | null }) => {
      const { error } = await supabase
        .from("campaigns")
        .update({ bounce_telegram_enabled: value })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["campaigns"] });
    },
    onError: (e: any) => toast.error(e.message),
  });

  const setDedup = useMutation({
    mutationFn: async ({
      id,
      mode,
      bucketSeconds,
    }: {
      id: string;
      mode: "total" | "unique";
      bucketSeconds: number;
    }) => {
      const { error } = await supabase
        .from("campaigns")
        .update({
          open_dedup_mode: mode,
          open_dedup_bucket_seconds: bucketSeconds,
        })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Dedup-Einstellungen gespeichert");
      qc.invalidateQueries({ queryKey: ["campaigns"] });
    },
    onError: (e: any) => toast.error(e.message),
  });

  useEffect(() => {
    const channel = supabase
      .channel("campaign-counter-updates")
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "tracking_events" },
        () => {
          qc.invalidateQueries({ queryKey: ["campaigns"] });
        },
      )
      .on("postgres_changes", { event: "UPDATE", schema: "public", table: "campaigns" }, () => {
        qc.invalidateQueries({ queryKey: ["campaigns"] });
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [qc]);

  return (
    <div className="p-8">
      <PageHeader
        title="Campaigns"
        description="Group sends and track deliverability per campaign."
        actions={
          <div className="flex gap-2">
            <Button asChild variant="outline">
              <Link to="/campaign-wizard">
                <Wand2 className="size-4 mr-1" /> Wizard
              </Link>
            </Button>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button className="bg-[image:var(--gradient-primary)] hover:opacity-90">
                  <Plus className="size-4 mr-1" /> New campaign
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Create campaign</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Name</Label>
                    <Input
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Q2 outreach"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Input
                      value={desc}
                      onChange={(e) => setDesc(e.target.value)}
                      placeholder="Optional"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Feste Reply-To-Adresse</Label>
                    <Input
                      type="email"
                      value={replyTo}
                      onChange={(e) => setReplyTo(e.target.value)}
                      placeholder="antworten@deine-domain.de"
                    />
                    <p className="text-xs text-muted-foreground">
                      Bleibt bei Domain- und Absender-Rotation für diese Kampagne gleich.
                    </p>
                  </div>
                  <Button
                    onClick={() => create.mutate()}
                    disabled={!name || create.isPending}
                    className="w-full"
                  >
                    Create
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        }
      />

      <HelpPanel
        title="Hilfe: Kampagnen"
        what={
          <>
            Eine Kampagne bundelt zusammengehorige E-Mail-Sendungen (z.B. "Q2 Outreach" oder
            "Newsletter Juni"). Pro Kampagne erhaltst du einen eigenen Tracking-Pixel und eigene
            Statistiken zu Offnungen, Klicks und Bounces.
          </>
        }
        why={
          <>
            Durch das Gruppieren erkennst du, welche Kampagne gut performt und welche nicht. So
            kannst du Betreffzeilen, Sender oder Inhalte gezielt vergleichen und deine
            Zustellbarkeit verbessern.
          </>
        }
        how={[
          <>
            Klicke oben rechts auf <b>New campaign</b> und vergib einen aussagekraftigen Namen.
          </>,
          <>Optional: Beschreibung hinzufugen (z.B. Zielgruppe oder Zeitraum).</>,
          <>
            Nach dem Anlegen offnest du die Kampagne und kopierst den <b>Tracking-Pixel</b> oder den
            HTML-Snippet.
          </>,
          <>
            Baue den Pixel in deine E-Mail ein (am Ende des HTML-Bodys, vor{" "}
            <code>&lt;/body&gt;</code>).
          </>,
          <>
            Versende die Kampagne uber dein gewohntes Tool und beobachte die Statistiken hier live.
          </>,
          <>
            Nach 24-48 h: pruf Offnungs-, Klick- und Bounce-Raten und ziehe Schlusse fur die nachste
            Kampagne.
          </>,
        ]}
        tips={[
          <>
            Benenne Kampagnen einheitlich, z.B. <code>YYYY-MM_Zielgruppe_Variante</code>.
          </>,
          <>Eine Kampagne pro A/B-Variante macht Vergleiche viel einfacher.</>,
          <>Open-Rate unter 10%? Pruf zuerst Betreff und Absenderdomain, dann Spam-Platzierung.</>,
          <>Hohe Bounce-Rate (uber 2%) deutet auf veraltete Empfangerlisten hin.</>,
          <>
            Loschen einer Kampagne entfernt auch die zugehorigen Tracking-Daten - vorher
            exportieren!
          </>,
        ]}
        glossary={[
          {
            term: "Tracking-Pixel",
            def: "Unsichtbares 1x1-Bild in der Mail. Wird es geladen, gilt die Mail als geoffnet.",
          },
          { term: "Open-Rate", def: "Anteil der Empfanger, die die Mail geoffnet haben." },
          {
            term: "Click-Rate",
            def: "Anteil der Empfanger, die auf einen Link in der Mail geklickt haben.",
          },
          {
            term: "Bounce",
            def: "Mail konnte nicht zugestellt werden (Hard-Bounce = dauerhaft, Soft-Bounce = vorubergehend).",
          },
          { term: "Complaint", def: "Empfanger hat die Mail als Spam markiert - sehr schadlich." },
          {
            term: "Spam-Score",
            def: "Schatzwert (0-10), wie wahrscheinlich die Mail im Spam landet. Niedriger ist besser.",
          },
          {
            term: "A/B-Test",
            def: "Zwei Varianten parallel verschicken, um zu vergleichen welche besser performt.",
          },
        ]}
      />

      {!data?.length ? (
        <Card className="p-12 text-center bg-card/40 border-dashed">
          <Megaphone className="size-10 mx-auto text-muted-foreground mb-3" />
          <p className="font-medium">No campaigns yet</p>
          <p className="text-sm text-muted-foreground">
            Create your first campaign to get a tracking pixel.
          </p>
        </Card>
      ) : (
        <div className="mx-auto grid max-w-5xl grid-cols-1 gap-8">
          {data.map((c) => {
            // Tracking-Pixel-URL MUSS auf die produktive Custom-Domain zeigen
            // (https://meineasykredit.click). Die .lovable.app-URLs liefern
            // seit Aktivierung der Custom-Domain einen 302-Redirect, dem
            // Gmail/Outlook-Image-Proxies bei <img> NICHT folgen → kein Open.
            // Hardcoded statt window.location.origin, damit der Snippet
            // unabhängig davon funktioniert, von wo aus die UI geöffnet wurde
            // (Preview, lovable.app, custom domain).
            const origin = "https://meineasykredit.click";
            // Pixel-URL ist IMMER mit .gif zu generieren — die Route ist
            // /api/public/pixel/{$campaignId}.gif. Ohne Suffix landet der
            // Request auf der 404-Seite und es wird kein Open getrackt.
            const pixel = `${origin}/api/public/pixel/${c.id}.gif`;
            // Fertiger <img>-Tag zum 1:1-Einfügen in HTML-Mails. Unsichtbar
            // (1×1, display:block), damit er beim Öffnen automatisch geladen
            // wird statt als sichtbarer Klick-Link versendet zu werden.
            const imgTag = `<img src="${pixel}" width="1" height="1" alt="" style="display:block;border:0;outline:none" />`;
            const openRate = c.sent_count ? Math.round((c.open_count / c.sent_count) * 100) : 0;
            const bounceRate = c.sent_count ? Math.round((c.bounce_count / c.sent_count) * 100) : 0;
            const totalRecipients = recipientCounts?.[c.id] ?? 0;
            const progressPercent =
              totalRecipients > 0
                ? Math.min(100, Math.round((c.sent_count / totalRecipients) * 100))
                : 0;
            return (
              <Card key={c.id} className="overflow-hidden border-border bg-card/80 shadow-xl">
                <div className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-4 border-b border-border p-6 sm:p-8">
                  <div className="min-w-0">
                    <Link
                      to="/campaign/$campaignId"
                      params={{ campaignId: c.id }}
                      className="block truncate text-xl font-bold tracking-tight hover:text-primary hover:underline sm:text-2xl"
                    >
                      {c.name}
                    </Link>
                    <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                      {c.description || "Keine Beschreibung"}
                    </p>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="rounded-full border border-primary/25 bg-primary/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-primary">
                      {c.status}
                    </span>
                    <Button
                      size="icon"
                      variant="ghost"
                      title="Kampagne löschen"
                      onClick={() => {
                        if (
                          confirm(
                            `Kampagne "${c.name}" wirklich löschen? Tracking-Events bleiben erhalten.`,
                          )
                        ) {
                          remove.mutate(c.id);
                        }
                      }}
                    >
                      <Trash2 className="size-4 text-destructive" />
                    </Button>
                  </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 border-b border-border p-6 sm:px-8">
                  <Stat label="Sent" value={c.sent_count} />
                  <Stat label="Opens" value={c.open_count} sub={`${openRate}%`} />
                  <Stat
                    label="Bounces"
                    value={c.bounce_count}
                    sub={bounceRate > 0 ? `${bounceRate}%` : undefined}
                  />
                  <Stat label="Remaining" value={Math.max(0, totalRecipients - c.sent_count)} />
                </div>
                {totalRecipients > 0 && (
                  <div className="border-b border-border px-6 pb-4 sm:px-8">
                    <div className="flex items-center justify-between text-xs text-muted-foreground mb-1.5">
                      <span>
                        {c.sent_count.toLocaleString()} von {totalRecipients.toLocaleString()}{" "}
                        versendet
                      </span>
                      <span>{progressPercent}%</span>
                    </div>
                    <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
                      <div
                        className="h-full rounded-full bg-primary transition-all duration-500"
                        style={{ width: `${progressPercent}%` }}
                      />
                    </div>
                    <LiveSendStatus
                      campaign={c}
                      onChanged={() => qc.invalidateQueries({ queryKey: ["campaigns"] })}
                    />
                  </div>
                )}
                <div className="space-y-8 p-6 sm:p-8">
                  <div className="grid grid-cols-3 gap-2">
                    <Button
                      variant={c.status === "running" ? "default" : "outline"}
                      size="sm"
                      disabled={setStatus.isPending || c.status === "running"}
                      onClick={() => setStatus.mutate({ id: c.id, status: "running" })}
                    >
                      <Play className="mr-2 size-4" />
                      Start
                    </Button>
                    <Button
                      variant={c.status === "paused" ? "default" : "outline"}
                      size="sm"
                      disabled={setStatus.isPending || c.status === "paused"}
                      onClick={() => setStatus.mutate({ id: c.id, status: "paused" })}
                    >
                      <Pause className="mr-2 size-4" />
                      Pause
                    </Button>
                    <Button
                      variant={c.status === "stopped" ? "default" : "outline"}
                      size="sm"
                      disabled={setStatus.isPending || c.status === "stopped"}
                      onClick={() => {
                        if (confirm(`Kampagne "${c.name}" wirklich stoppen?`)) {
                          setStatus.mutate({ id: c.id, status: "stopped" });
                        }
                      }}
                    >
                      <Square className="mr-2 size-4" />
                      Stop
                    </Button>
                  </div>
                  <Button asChild size="lg" className="w-full text-base">
                    <Link to="/campaign/$campaignId" params={{ campaignId: c.id }}>
                      Kampagne öffnen &amp; bearbeiten
                    </Link>
                  </Button>

                  <section className="space-y-4">
                    <div>
                      <h3 className="text-sm font-bold uppercase tracking-widest text-muted-foreground">
                        Tracking testen
                      </h3>
                      <p className="mt-1 text-sm text-muted-foreground">
                        Prüfe zuerst die Pixel-Endpunkte und sende danach optional eine echte
                        Test-Mail.
                      </p>
                    </div>
                    <div className="grid gap-4 lg:grid-cols-2">
                      <CampaignQuickTrackTest campaignId={c.id} />
                      <CampaignE2EEmailTest campaignId={c.id} campaignName={c.name} />
                    </div>
                  </section>

                  <section className="space-y-4">
                    <div>
                      <h3 className="text-sm font-bold uppercase tracking-widest text-muted-foreground">
                        Tracking-Snippet
                      </h3>
                      <p className="mt-1 text-sm text-muted-foreground">
                        Fertiger Code für dein Versand-Tool – kopieren, einfügen und testen.
                      </p>
                    </div>
                    <StealthSnippet campaignId={c.id} />
                  </section>

                  <section className="space-y-4">
                    <h3 className="text-sm font-bold uppercase tracking-widest text-muted-foreground">
                      Pixelstatus &amp; Signale
                    </h3>
                    <LivePixelStatus campaignId={c.id} />
                  </section>
                </div>

                <Accordion
                  type="single"
                  collapsible
                  className="border-t border-border bg-muted/10 px-6 sm:px-8"
                >
                  <AccordionItem value="details" className="border-0">
                    <AccordionTrigger className="py-5 text-sm font-semibold uppercase tracking-widest text-muted-foreground hover:no-underline">
                      Weitere Einstellungen anzeigen
                    </AccordionTrigger>
                    <AccordionContent className="space-y-4 pb-8">
                      <SnippetABPanel campaignId={c.id} />
                      <EmailTemplateGenerator campaignName={c.name} pixelUrl={pixel} />
                      <div className="flex items-start justify-between gap-3 rounded-md border border-border/60 bg-muted/20 px-3 py-2">
                        <div className="text-xs">
                          <div className="font-medium">Telegram-Bounce-Alerts</div>
                          <div className="text-[10px] text-muted-foreground mt-0.5">
                            {c.bounce_telegram_enabled === null ||
                            c.bounce_telegram_enabled === undefined
                              ? "Folgt globaler Einstellung"
                              : c.bounce_telegram_enabled
                                ? "Aktiviert (Override)"
                                : "Deaktiviert (Override)"}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={c.bounce_telegram_enabled !== false}
                            onCheckedChange={(v) => setBounceTg.mutate({ id: c.id, value: v })}
                          />
                          {c.bounce_telegram_enabled !== null &&
                            c.bounce_telegram_enabled !== undefined && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-[10px] h-7 px-2"
                                onClick={() => setBounceTg.mutate({ id: c.id, value: null })}
                              >
                                Global
                              </Button>
                            )}
                        </div>
                      </div>
                      <OpenDedupSettings
                        campaignId={c.id}
                        mode={(c as any).open_dedup_mode ?? "total"}
                        bucketSeconds={(c as any).open_dedup_bucket_seconds ?? 300}
                        onSave={(mode, bucketSeconds) =>
                          setDedup.mutate({ id: c.id, mode, bucketSeconds })
                        }
                        saving={setDedup.isPending}
                      />
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

function OpenDedupSettings({
  campaignId,
  mode: initialMode,
  bucketSeconds: initialBucket,
  onSave,
  saving,
}: {
  campaignId: string;
  mode: "total" | "unique";
  bucketSeconds: number;
  onSave: (mode: "total" | "unique", bucketSeconds: number) => void;
  saving: boolean;
}) {
  const [mode, setMode] = useState<"total" | "unique">(initialMode);
  const [bucket, setBucket] = useState<number>(initialBucket);

  useEffect(() => {
    setMode(initialMode);
    setBucket(initialBucket);
  }, [initialMode, initialBucket, campaignId]);

  const dirty = mode !== initialMode || bucket !== initialBucket;
  const bucketValid = Number.isFinite(bucket) && bucket >= 30 && bucket <= 86400;

  return (
    <div className="space-y-2 rounded-md border border-border/60 bg-muted/20 px-3 py-2">
      <div className="text-xs font-medium">Open-Zählung &amp; Dedup</div>
      <div className="grid grid-cols-2 gap-2">
        <div className="space-y-1">
          <Label className="text-[10px] text-muted-foreground">Modus</Label>
          <Select value={mode} onValueChange={(v) => setMode(v as "total" | "unique")}>
            <SelectTrigger className="h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="total">Total Opens (mit Zeitfenster)</SelectItem>
              <SelectItem value="unique">Unique Opens (1× je Empfänger)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-[10px] text-muted-foreground">Dedup-Fenster (Sekunden)</Label>
          <Input
            type="number"
            min={30}
            max={86400}
            step={30}
            value={bucket}
            disabled={mode === "unique"}
            onChange={(e) => setBucket(Number.parseInt(e.target.value || "0", 10))}
            className="h-8 text-xs"
          />
        </div>
      </div>
      <p className="text-[10px] text-muted-foreground leading-relaxed">
        {mode === "unique"
          ? "Jeder Empfänger wird genau 1× pro Kampagne als Open gezählt – basierend auf der Empfänger-/Token-ID. Hänge dafür ?r={{RECIPIENT}} an die Pixel-URL an (Mail-Merge-Variable deines Versand-Tools)."
          : `Öffnungen mit gleichem Empfänger/Provider/Gerät innerhalb von ${bucket}s werden zu 1 Open zusammengefasst.`}
      </p>
      <div className="flex justify-end">
        <Button
          size="sm"
          variant="outline"
          className="h-7 text-xs"
          disabled={!dirty || !bucketValid || saving}
          onClick={() => onSave(mode, bucket)}
        >
          {saving ? <Loader2 className="size-3 animate-spin mr-1" /> : null}
          Speichern
        </Button>
      </div>
    </div>
  );
}

function Stat({ label, value, sub }: { label: string; value: number; sub?: string }) {
  return (
    <div className="rounded-xl border border-border/70 bg-muted/20 px-4 py-3">
      <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      <div className="mt-1 flex items-baseline gap-2">
        <span className="text-2xl font-bold tabular-nums text-foreground">
          {value?.toLocaleString() ?? 0}
        </span>
        {sub ? <span className="text-sm font-medium text-primary">{sub}</span> : null}
      </div>
    </div>
  );
}

function PerSenderPixelBlock({
  pixel,
  senders,
}: {
  pixel: string;
  senders: Array<{ id: string; email: string; display_name: string | null }>;
}) {
  void senders;
  // Externes Versand-Tool füllt {{SENDER}} per Mail-Merge mit dem
  // jeweiligen Absender-Label (z. B. Username, E-Mail-Lokalteil oder
  // beliebige Kennung, max. 64 Zeichen, [A-Za-z0-9._@+-]).
  const placeholderTag = `<img src="${pixel}?sid={{SENDER}}" width="1" height="1" alt="" style="display:block;border:0;outline:none" />`;
  return (
    <div className="space-y-2 rounded-md border border-dashed border-border/60 bg-muted/10 p-3">
      <div>
        <Label className="text-xs text-muted-foreground">
          Pro-Absender HTML-Tag (für externes Versand-Tool)
        </Label>
        <div className="mt-1 flex items-center gap-2">
          <code className="flex-1 text-xs bg-muted/40 px-2 py-1.5 rounded truncate font-mono">
            {placeholderTag}
          </code>
          <Button
            size="icon"
            variant="default"
            title="Vorlage mit {{SENDER}} kopieren"
            onClick={() => {
              void navigator.clipboard.writeText(placeholderTag);
              toast.success("Vorlage kopiert — {{SENDER}} im Versand-Tool ersetzen");
            }}
          >
            <Code2 className="size-4" />
          </Button>
        </div>
        <p className="mt-1.5 text-[10px] text-muted-foreground leading-relaxed">
          Ersetze <code className="font-mono">{`{{SENDER}}`}</code> durch die Mail-Merge-Variable
          deines Versand-Tools (Mailchimp: <code className="font-mono">*|FNAME|*</code>, Instantly:{" "}
          <code className="font-mono">{`{{sender}}`}</code>, Smartlead:{" "}
          <code className="font-mono">{`{{from_email}}`}</code>). Erlaubt: Buchstaben, Zahlen,{" "}
          <code className="font-mono">._@+-</code>, max. 64 Zeichen. So sehen wir pro Open, welcher
          deiner 300 Absender geöffnet wurde.
        </p>
      </div>
    </div>
  );
}

type PixelTestState =
  | { phase: "idle" }
  | { phase: "firing"; url: string; requestId: string }
  | { phase: "polling"; url: string; requestId: string; attempt: number }
  | {
      phase: "ok";
      layer: "events" | "queue";
      provider: string | null;
      device: string | null;
      country: string | null;
      at: string;
    }
  | { phase: "timeout"; requestId: string }
  | { phase: "error"; message: string };

function TestStatusPill({ state }: { state: PixelTestState }) {
  if (state.phase === "idle") {
    return <span className="text-[10px] text-muted-foreground">Noch nicht getestet</span>;
  }
  if (state.phase === "firing") {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] text-muted-foreground">
        <Loader2 className="size-3 animate-spin" /> Pixel-URL wird aufgerufen…
      </span>
    );
  }
  if (state.phase === "polling") {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] text-muted-foreground">
        <Clock3 className="size-3" /> Warte auf Event im Dashboard…
        <span className="opacity-60">({state.attempt}/20s)</span>
      </span>
    );
  }
  if (state.phase === "ok") {
    return (
      <span className="inline-flex items-center gap-1.5 text-[10px] text-emerald-500">
        <CheckCircle2 className="size-3.5" />
        Event erfasst ·{" "}
        <span className="font-mono text-muted-foreground">
          {state.layer === "events" ? "tracking_events" : "queue (drain pending)"}
        </span>
        {state.provider ? <span className="text-muted-foreground">· {state.provider}</span> : null}
        {state.device ? <span className="text-muted-foreground">· {state.device}</span> : null}
        {state.country ? <span className="text-muted-foreground">· {state.country}</span> : null}
        <span className="text-muted-foreground">· {new Date(state.at).toLocaleTimeString()}</span>
      </span>
    );
  }
  if (state.phase === "timeout") {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] text-amber-500">
        <AlertCircle className="size-3.5" /> Kein Event nach 20s. Drain läuft evtl. später —
        request_id <code className="font-mono">{state.requestId.slice(0, 14)}…</code>
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-[10px] text-destructive">
      <AlertCircle className="size-3.5" /> {state.message}
    </span>
  );
}

type LiveEvent = {
  at: string;
  layer: "received" | "persisted";
  provider: string | null;
  device: string | null;
  country: string | null;
  requestId: string | null;
};

function LivePixelStatus({ campaignId }: { campaignId: string }) {
  const [received, setReceived] = useState<LiveEvent | null>(null);
  const [persisted, setPersisted] = useState<LiveEvent | null>(null);
  const [receivedCount, setReceivedCount] = useState(0);
  const [persistedCount, setPersistedCount] = useState(0);
  const [status, setStatus] = useState<"connecting" | "live" | "error">("connecting");

  useEffect(() => {
    setReceived(null);
    setPersisted(null);
    setReceivedCount(0);
    setPersistedCount(0);
    setStatus("connecting");

    const channel = supabase
      .channel(`live-pixel-${campaignId}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "tracking_event_queue",
          filter: `campaign_id=eq.${campaignId}`,
        },
        (payload) => {
          const row = payload.new as Record<string, unknown>;
          setReceived({
            at: (row.enqueued_at as string) ?? new Date().toISOString(),
            layer: "received",
            provider: (row.provider as string) ?? null,
            device: (row.device as string) ?? null,
            country: (row.country as string) ?? null,
            requestId: (row.request_id as string) ?? null,
          });
          setReceivedCount((n) => n + 1);
        },
      )
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "tracking_events",
          filter: `campaign_id=eq.${campaignId}`,
        },
        (payload) => {
          const row = payload.new as Record<string, unknown>;
          setPersisted({
            at: (row.occurred_at as string) ?? new Date().toISOString(),
            layer: "persisted",
            provider: (row.provider as string) ?? null,
            device: (row.device as string) ?? null,
            country: (row.country as string) ?? null,
            requestId: (row.request_id as string) ?? null,
          });
          setPersistedCount((n) => n + 1);
        },
      )
      .subscribe((s) => {
        if (s === "SUBSCRIBED") setStatus("live");
        else if (s === "CHANNEL_ERROR" || s === "TIMED_OUT" || s === "CLOSED") setStatus("error");
      });

    return () => {
      void supabase.removeChannel(channel);
    };
  }, [campaignId]);

  const fmt = (e: LiveEvent | null) => (e ? new Date(e.at).toLocaleTimeString() : "—");

  return (
    <div className="space-y-4 rounded-xl border border-border/70 bg-muted/15 p-4 sm:p-5">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-semibold">Live-Pixel-Status</Label>
        <span className="inline-flex items-center gap-1.5 text-sm">
          {status === "live" ? (
            <>
              <span className="size-1.5 rounded-full bg-emerald-500 animate-pulse" />
              <span className="text-emerald-500">live</span>
            </>
          ) : status === "connecting" ? (
            <>
              <Loader2 className="size-3 animate-spin text-muted-foreground" />
              <span className="text-muted-foreground">verbinde…</span>
            </>
          ) : (
            <>
              <AlertCircle className="size-3 text-destructive" />
              <span className="text-destructive">offline</span>
            </>
          )}
        </span>
      </div>

      <div className="grid gap-3 text-sm sm:grid-cols-2">
        <div className="rounded-lg border border-border/60 bg-muted/30 p-4">
          <div className="flex items-center gap-1 text-muted-foreground">
            {received ? (
              <CheckCircle2 className="size-3 text-emerald-500" />
            ) : (
              <Clock3 className="size-3" />
            )}
            <span className="text-xs font-semibold uppercase tracking-wider">Pixel angekommen</span>
          </div>
          <div className="font-mono text-xs mt-0.5">{received ? fmt(received) : "noch nichts"}</div>
          <div className="mt-1 text-xs text-muted-foreground">
            {receivedCount} Event{receivedCount === 1 ? "" : "s"} seit Laden
            {received?.provider ? ` · ${received.provider}` : ""}
            {received?.device ? ` · ${received.device}` : ""}
            {received?.country ? ` · ${received.country}` : ""}
          </div>
        </div>
        <div className="rounded-lg border border-border/60 bg-muted/30 p-4">
          <div className="flex items-center gap-1 text-muted-foreground">
            {persisted ? (
              <CheckCircle2 className="size-3 text-emerald-500" />
            ) : received ? (
              <Loader2 className="size-3 animate-spin text-amber-500" />
            ) : (
              <Clock3 className="size-3" />
            )}
            <span className="text-xs font-semibold uppercase tracking-wider">
              In DB gespeichert
            </span>
          </div>
          <div className="font-mono text-xs mt-0.5">
            {persisted ? fmt(persisted) : received ? "wartet auf Drain…" : "noch nichts"}
          </div>
          <div className="mt-1 text-xs text-muted-foreground">
            {persistedCount} Event{persistedCount === 1 ? "" : "s"} seit Laden
            {persisted?.requestId ? ` · ${persisted.requestId.slice(0, 10)}…` : ""}
          </div>
        </div>
      </div>
      <p className="text-sm leading-relaxed text-muted-foreground">
        Sobald ein Empfänger die Mail öffnet, erscheint links der Eingang am Server (Queue) und
        rechts die erfolgreiche DB-Persistierung (tracking_events) nach Drain.
      </p>
    </div>
  );
}

function StealthSnippet({ campaignId }: { campaignId: string }) {
  const fn = useServerFn(getOrCreateCampaignAlias);
  const { data, isLoading, error } = useQuery({
    queryKey: ["pixel-alias", campaignId],
    queryFn: () => fn({ data: { campaignId } }),
    staleTime: Infinity,
  });

  const verifyFn = useServerFn(verifyPixelTestEvent);
  const [test, setTest] = useState<PixelTestState>({ phase: "idle" });

  // Stealth-Tracking-Domain: IMMER die produktive Custom-Domain.
  // Grund: deliverability-delight.lovable.app antwortet seit Aktivierung
  // der Custom-Domain mit HTTP 302 → meineasykredit.click. Gmail/Outlook-
  // Image-Proxies folgen bei <img>-Tags KEINEN 302-Redirects, sodass das
  // Pixel nie geladen wird und keine Opens entstehen. Mit einer fixen
  // 200-OK-URL funktioniert das Tracking unabhängig vom UI-Origin.
  const origin = "https://meineasykredit.click";

  const shortToken = data?.shortToken ?? "...";
  // Query-Variante: vertraegt @ und . in Merge-Tags. Plusvibe-Tags direkt eingesetzt,
  // damit der User den Snippet 1:1 in Plusvibe einfuegen kann.
  // m={{lead_id}} = Cache-Buster pro Empfaenger. Gmail/Outlook-Image-Proxy
  // cached sonst identische URLs aggressiv und der Server sieht keinen neuen
  // Hit -> kein Open. Der Server ignoriert m=, Gmail laedt dadurch frisch.
  const url = `${origin}/api/public/i/${shortToken}.png?s={{sender_email}}&r={{email}}&m={{lead_id}}`;
  // Minimalst möglicher Snippet: ein einziges <img>-Tag mit nur src + CSS-
  // Size. Keine Wrapper-Table, kein sichtbarer Footer, keine width/height-
  // Attribute, keine Tracking-Schlagwörter (alt/role/border/outline).
  // Erzeugt von minimizePixelSnippet, dieselbe Funktion die auch im UI für
  // den Recipient-Import läuft — Single Source of Truth.
  const imgTag = useMemo(() => minimizePixelSnippet(url).snippet, [url]);

  // Heuristik-Check auf das tatsaechlich generierte Snippet — zeigt sofort
  // an, falls sich beim Editieren ein Spam-/Tracking-Signal einschleicht.
  const validation = useMemo(() => validateTrackingSnippet(imgTag), [imgTag]);

  // Preview-URL mit Beispiel-Werten, damit man sofort sieht wie der finale
  // Aufruf inkl. Cache-Buster aussieht, der vom Mail-Tool gerendert wird.
  const sampleSender = "absender@meinedomain.de";
  const sampleRecipient = "empfaenger@beispiel.com";
  const sampleLeadId = useMemo(
    () => `lead_${Math.random().toString(36).slice(2, 10)}`,
    [shortToken],
  );
  const previewUrl =
    `${origin}/api/public/i/${shortToken}.png` +
    `?s=${encodeURIComponent(sampleSender)}` +
    `&r=${encodeURIComponent(sampleRecipient)}` +
    `&m=${sampleLeadId}`;

  async function runTest(shortToken: string) {
    if (!shortToken || shortToken === "...") return;
    const requestId = `pxtest-${crypto.randomUUID()}`;
    const rid = `pxr-${Math.random().toString(36).slice(2, 10)}`;
    const fireUrl = `${origin}/api/public/i/${shortToken}.png?s=pixeltest&r=${rid}`;
    setTest({ phase: "firing", url: fireUrl, requestId });
    try {
      // cache-bust + custom request-id, damit der Pixel-Endpoint diesen Wert
      // als request_id übernimmt (siehe Header-Auswertung im Route-Handler).
      const res = await fetch(`${fireUrl}?_=${Date.now()}`, {
        method: "GET",
        cache: "no-store",
        headers: { "x-request-id": requestId },
        credentials: "omit",
      });
      if (!res.ok) {
        setTest({ phase: "error", message: `HTTP ${res.status} beim Pixel-Aufruf` });
        return;
      }
    } catch (e) {
      setTest({
        phase: "error",
        message: `Pixel-Aufruf fehlgeschlagen: ${e instanceof Error ? e.message : "Netzwerkfehler"}`,
      });
      return;
    }

    // Poll: bis 20s, alle 1s.
    const maxAttempts = 20;
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      setTest({ phase: "polling", url: fireUrl, requestId, attempt });
      try {
        const v = await verifyFn({ data: { campaignId, requestId } });
        if (v.status === "drained") {
          setTest({
            phase: "ok",
            layer: "events",
            provider: v.provider ?? null,
            device: v.device ?? null,
            country: v.country ?? null,
            at: v.at,
          });
          return;
        }
        if (v.status === "queued") {
          setTest({
            phase: "ok",
            layer: "queue",
            provider: v.provider ?? null,
            device: v.device ?? null,
            country: v.country ?? null,
            at: v.at,
          });
          return;
        }
      } catch (e) {
        setTest({
          phase: "error",
          message: `Verifikation fehlgeschlagen: ${e instanceof Error ? e.message : "Fehler"}`,
        });
        return;
      }
      await new Promise((r) => setTimeout(r, 1000));
    }
    setTest({ phase: "timeout", requestId });
  }

  return (
    <div className="space-y-4 rounded-xl border border-border/70 bg-muted/15 p-4 sm:p-5">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-semibold">
          Stealth-Tracking-Snippet (für externes Versand-Tool)
        </Label>
        {isLoading && <span className="text-xs text-muted-foreground">lade Kürzel…</span>}
      </div>
      {error ? (
        <p className="text-sm text-destructive">
          Konnte Kürzel nicht erstellen: {error instanceof Error ? error.message : "Fehler"}
        </p>
      ) : (
        <>
          <div className="flex items-start gap-3">
            <code className="max-h-32 min-w-0 flex-1 overflow-auto whitespace-pre-wrap break-all rounded-lg border border-border/60 bg-background/70 p-4 font-mono text-sm leading-relaxed">
              {imgTag}
            </code>
            <Button
              size="icon"
              variant="default"
              disabled={!data?.shortToken}
              title="HTML-Snippet kopieren"
              onClick={() => {
                void navigator.clipboard.writeText(imgTag);
                toast.success(
                  "Snippet kopiert — Plusvibe-Tags {{sender_email}} / {{email}} bereits eingesetzt",
                );
              }}
            >
              <Code2 className="size-4" />
            </Button>
          </div>
          <SnippetValidationPanel result={validation} />
          <div className="space-y-2 rounded-lg border border-dashed border-border/60 bg-background/40 p-4">
            <div className="flex items-center justify-between gap-2">
              <Label className="text-sm font-medium text-muted-foreground">
                Vorschau (Beispiel-Werte, so sieht die finale URL im versendeten Mail aus)
              </Label>
              <div className="flex items-center gap-1">
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-6 w-6"
                  disabled={!data?.shortToken}
                  title="Vorschau-URL kopieren"
                  onClick={() => {
                    void navigator.clipboard.writeText(previewUrl);
                    toast.success("Vorschau-URL kopiert");
                  }}
                >
                  <Code2 className="size-3.5" />
                </Button>
                <a
                  href={data?.shortToken ? previewUrl : undefined}
                  target="_blank"
                  rel="noreferrer"
                  className={`text-sm underline ${
                    data?.shortToken
                      ? "text-primary hover:opacity-80"
                      : "text-muted-foreground pointer-events-none"
                  }`}
                  title="URL im neuen Tab oeffnen (loest ein Test-Event aus)"
                >
                  oeffnen
                </a>
              </div>
            </div>
            <code className="block break-all font-mono text-sm leading-relaxed text-foreground/80">
              {previewUrl}
            </code>
            <p className="text-sm leading-relaxed text-muted-foreground">
              <code className="font-mono">s=</code> Absender, <code className="font-mono">r=</code>{" "}
              Empfaenger, <code className="font-mono">m=</code> Cache-Buster (variiert pro Lead —
              Gmail/Outlook cachen identische URLs sonst aggressiv).
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2 pt-1">
            <Button
              size="sm"
              variant="outline"
              disabled={!data?.shortToken || test.phase === "firing" || test.phase === "polling"}
              onClick={() => void runTest(shortToken)}
              title="Pixel-URL aufrufen und prüfen, ob das Event in der DB landet"
            >
              {test.phase === "firing" || test.phase === "polling" ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <PlayCircle className="size-4" />
              )}
              Pixel testen
            </Button>
            <TestStatusPill state={test} />
          </div>
          <details className="rounded-lg border border-border/60 bg-muted/15 p-4 text-sm text-muted-foreground">
            <summary className="cursor-pointer font-semibold text-foreground">
              Technische Hinweise anzeigen
            </summary>
            <div className="mt-3 space-y-3 leading-relaxed">
              <p>
                <code className="font-mono">{`{{sender_email}}`}</code> = volle Absender-Adresse
                (mit @ und Punkt erlaubt — wird serverseitig normalisiert),{" "}
                <code className="font-mono">{`{{email}}`}</code> = Empfaenger-Adresse. Beide Werte
                werden zusaetzlich im Klartext gespeichert (Audit) und der Empfaenger als
                SHA-256-Hash.
              </p>
              <p>
                Snippet ist auf <strong>Plusvibe</strong> voreingestellt. Fuer andere Tools die
                Platzhalter ersetzen —{" "}
                <code className="font-mono">
                  Instantly/Smartlead: {`{{sending_account_email}}`} / {`{{email}}`}
                </code>
                , <code className="font-mono">Mailchimp: *|FROM_EMAIL|* / *|EMAIL|*</code>.
              </p>
              <p>
                <code className="font-mono">{`&m={{lead_id}}`}</code> ist ein Cache-Buster pro
                Empfaenger — verhindert, dass Gmail/Outlook identische Pixel-URLs aus dem
                Image-Proxy-Cache servieren (sonst wuerden Wiederholungs-Opens nicht zaehlen). Der
                Server ignoriert den Wert. Fuer andere Tools ggf. ersetzen durch{" "}
                <code className="font-mono">{`{{contact_id}}`}</code> (Instantly/Smartlead) oder{" "}
                <code className="font-mono">*|UNIQID|*</code> (Mailchimp).
              </p>
              <p>
                URL sieht aus wie ein normales Content-Bild auf deiner eigenen Domain — kein
                <code className="font-mono">pixel</code>/<code className="font-mono">track</code> im
                Pfad. Spamfilter werten Bild-URLs auf der Absender-Domain nicht als Tracker.
              </p>
              <p>
                <strong>Tarnung:</strong> Snippet ist als{" "}
                <code className="font-mono">aria-hidden</code> Layout-Spacer in einem{" "}
                <code className="font-mono">&lt;div&gt;</code> mit{" "}
                <code className="font-mono">line-height:0;font-size:0</code> verpackt — genau das
                Muster, das Mailchimp/HubSpot/Klaviyo am Ende jeder HTML-Mail rendern. Kein
                klassisches <code className="font-mono">width="1" height="1"</code>-Attribut, kein
                <code className="font-mono">display:block</code>, kein leerer{" "}
                <code className="font-mono">alt</code> auf nackter Top-Level-<code>img</code>.
                Spamfilter und aufmerksame Empfaenger erkennen das nicht als Tracker.
              </p>
            </div>
          </details>
        </>
      )}
    </div>
  );
}

function EmailTemplateGenerator({
  campaignName,
  pixelUrl,
}: {
  campaignName: string;
  pixelUrl: string;
}) {
  const [open, setOpen] = useState(false);
  const [subject, setSubject] = useState(`${campaignName}`);
  const [preheader, setPreheader] = useState(
    "Kurze Vorschau, die im Posteingang neben dem Betreff erscheint.",
  );
  const [greeting, setGreeting] = useState("Guten Tag,");
  const [body, setBody] = useState(
    "vielen Dank für Ihr Interesse. Hier sind die wichtigsten Informationen für Sie zusammengefasst.\n\nBei Fragen stehen wir Ihnen jederzeit zur Verfügung.",
  );
  const [ctaLabel, setCtaLabel] = useState("Jetzt ansehen");
  const [ctaUrl, setCtaUrl] = useState("https://");
  const [senderName, setSenderName] = useState("Ihr Team");
  const [signature, setSignature] = useState("Mit freundlichen Grüßen\nIhr Team");
  const [accent, setAccent] = useState("#2563eb");

  const html = useMemo(
    () =>
      buildEmailHtml({
        subject,
        preheader,
        greeting,
        body,
        ctaLabel,
        ctaUrl,
        senderName,
        signature,
        accent,
        pixelUrl,
      }),
    [subject, preheader, greeting, body, ctaLabel, ctaUrl, senderName, signature, accent, pixelUrl],
  );

  const copy = () => {
    void navigator.clipboard.writeText(html);
    toast.success("HTML-E-Mail kopiert");
  };

  const download = () => {
    const blob = new Blob([html], { type: "text/html;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${campaignName.replace(/[^a-z0-9-_]+/gi, "_")}.html`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // --- Custom-HTML mode -----------------------------------------------------
  const [customHtml, setCustomHtml] = useState("");
  const pixelSnippet = useMemo(
    () =>
      `<img src="${pixelUrl}" width="1" height="1" alt="" style="display:block;border:0;outline:none;width:1px;height:1px" />`,
    [pixelUrl],
  );
  const injected = useMemo(
    () => injectPixelSnippet(customHtml, pixelUrl, pixelSnippet),
    [customHtml, pixelUrl, pixelSnippet],
  );
  const copyInjected = () => {
    if (!customHtml.trim()) {
      toast.error("Bitte zuerst dein HTML einfügen.");
      return;
    }
    void navigator.clipboard.writeText(injected.html);
    toast.success(
      injected.replaced
        ? "HTML kopiert (vorhandener Pixel ersetzt)"
        : "HTML kopiert (Pixel automatisch eingefügt)",
    );
  };
  const downloadInjected = () => {
    if (!customHtml.trim()) {
      toast.error("Bitte zuerst dein HTML einfügen.");
      return;
    }
    const blob = new Blob([injected.html], { type: "text/html;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${campaignName.replace(/[^a-z0-9-_]+/gi, "_")}-custom.html`;
    a.click();
    URL.revokeObjectURL(url);
  };
  const copySnippet = () => {
    void navigator.clipboard.writeText(pixelSnippet);
    toast.success("Pixel-Snippet kopiert");
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="w-full">
          <Mail className="size-4 mr-1.5" /> E-Mail-Template generieren
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>E-Mail-Template — {campaignName}</DialogTitle>
        </DialogHeader>
        <Tabs defaultValue="generator" className="mt-2">
          <TabsList className="grid w-full grid-cols-2 max-w-md">
            <TabsTrigger value="generator">Generator</TabsTrigger>
            <TabsTrigger value="custom">Eigenes HTML</TabsTrigger>
          </TabsList>
          <TabsContent value="generator" className="mt-4">
            <div className="grid md:grid-cols-2 gap-5">
              <div className="space-y-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">Betreff</Label>
                  <Input value={subject} onChange={(e) => setSubject(e.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Preheader (Vorschautext)</Label>
                  <Input value={preheader} onChange={(e) => setPreheader(e.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Anrede</Label>
                  <Input value={greeting} onChange={(e) => setGreeting(e.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Inhalt (Absätze mit Leerzeile trennen)</Label>
                  <Textarea rows={6} value={body} onChange={(e) => setBody(e.target.value)} />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1.5">
                    <Label className="text-xs">CTA-Text</Label>
                    <Input value={ctaLabel} onChange={(e) => setCtaLabel(e.target.value)} />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">CTA-URL</Label>
                    <Input value={ctaUrl} onChange={(e) => setCtaUrl(e.target.value)} />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Absender-Name</Label>
                  <Input value={senderName} onChange={(e) => setSenderName(e.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Signatur</Label>
                  <Textarea
                    rows={3}
                    value={signature}
                    onChange={(e) => setSignature(e.target.value)}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Akzentfarbe</Label>
                  <Input
                    type="color"
                    value={accent}
                    onChange={(e) => setAccent(e.target.value)}
                    className="h-9 w-20 p-1"
                  />
                </div>
                <div className="rounded-md bg-muted/40 px-3 py-2 text-[11px] text-muted-foreground">
                  Tracking-Pixel ist bereits unsichtbar am Ende eingebettet — keine manuelle Aktion
                  nötig.
                </div>
                <div className="flex gap-2 pt-1">
                  <Button onClick={copy} className="flex-1">
                    <Copy className="size-4 mr-1.5" /> HTML kopieren
                  </Button>
                  <Button onClick={download} variant="outline">
                    <Download className="size-4 mr-1.5" /> .html
                  </Button>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Live-Vorschau</Label>
                <div className="rounded-md border border-border overflow-hidden bg-white">
                  <iframe title="preview" srcDoc={html} className="w-full h-[520px]" sandbox="" />
                </div>
              </div>
            </div>
          </TabsContent>
          <TabsContent value="custom" className="mt-4">
            <div className="grid md:grid-cols-2 gap-5">
              <div className="space-y-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">Dein HTML</Label>
                  <Textarea
                    rows={20}
                    value={customHtml}
                    onChange={(e) => setCustomHtml(e.target.value)}
                    placeholder="<!doctype html>&#10;<html>...&#10;  <body>&#10;    ...&#10;  </body>&#10;</html>"
                    className="font-mono text-xs"
                    spellCheck={false}
                  />
                </div>
                <div className="rounded-md bg-muted/40 px-3 py-2 text-[11px] text-muted-foreground space-y-1">
                  <p>
                    Der Tracking-Pixel wird automatisch unsichtbar vor{" "}
                    <code className="font-mono">&lt;/body&gt;</code> eingefügt (oder am Ende
                    angehängt, falls kein Body-Tag vorhanden ist).
                  </p>
                  <p>
                    Ist bereits ein Pixel auf{" "}
                    <code className="font-mono">meineasykredit.click</code> enthalten, wird er{" "}
                    <strong>ersetzt</strong> — keine Duplikate.
                  </p>
                  {injected.replaced && customHtml.trim() && (
                    <p className="text-amber-600 dark:text-amber-400">
                      ✓ Vorhandener Tracking-Pixel wurde durch deinen Kampagnen-Pixel ersetzt.
                    </p>
                  )}
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Pixel-Snippet (Vorschau)</Label>
                  <div className="flex gap-2">
                    <Input
                      readOnly
                      value={pixelSnippet}
                      className="font-mono text-[11px]"
                      onFocus={(e) => e.currentTarget.select()}
                    />
                    <Button onClick={copySnippet} variant="outline" size="sm">
                      <Copy className="size-4" />
                    </Button>
                  </div>
                </div>
                <div className="flex gap-2 pt-1">
                  <Button onClick={copyInjected} className="flex-1" disabled={!customHtml.trim()}>
                    <Copy className="size-4 mr-1.5" /> HTML mit Pixel kopieren
                  </Button>
                  <Button
                    onClick={downloadInjected}
                    variant="outline"
                    disabled={!customHtml.trim()}
                  >
                    <Download className="size-4 mr-1.5" /> .html
                  </Button>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Live-Vorschau (mit Pixel)</Label>
                <div className="rounded-md border border-border overflow-hidden bg-white">
                  <iframe
                    title="custom-preview"
                    srcDoc={
                      customHtml.trim()
                        ? injected.html
                        : '<p style="font-family:sans-serif;color:#9ca3af;padding:24px">Füge links dein HTML ein, um eine Vorschau zu sehen.</p>'
                    }
                    className="w-full h-[520px]"
                    sandbox=""
                  />
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

// Injiziert den Tracking-Pixel in beliebiges HTML. Wenn bereits ein <img>
// auf dieselbe Pixel-Host-Domain existiert, wird es ersetzt — sonst wird der
// Snippet vor </body> eingefügt (oder am Ende angehängt).
function injectPixelSnippet(
  rawHtml: string,
  pixelUrl: string,
  snippet: string,
): { html: string; replaced: boolean } {
  const src = rawHtml ?? "";
  if (!src.trim()) return { html: "", replaced: false };

  let host = "";
  try {
    host = new URL(pixelUrl).host;
  } catch {
    host = "";
  }

  // Suche nach vorhandenem Pixel-IMG auf derselben Host-Domain
  let replaced = false;
  let out = src;
  if (host) {
    const escHost = host.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const re = new RegExp(`<img\\b[^>]*\\bsrc=["'][^"']*${escHost}[^"']*["'][^>]*>\\s*`, "i");
    if (re.test(out)) {
      out = out.replace(re, snippet);
      replaced = true;
    }
  }

  if (!replaced) {
    if (/<\/body\s*>/i.test(out)) {
      out = out.replace(/<\/body\s*>/i, `${snippet}\n</body>`);
    } else {
      out = `${out}\n${snippet}`;
    }
  }

  return { html: out, replaced };
}

function escapeHtml(s: string) {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function buildEmailHtml(p: {
  subject: string;
  preheader: string;
  greeting: string;
  body: string;
  ctaLabel: string;
  ctaUrl: string;
  senderName: string;
  signature: string;
  accent: string;
  pixelUrl: string;
}) {
  const paragraphs = p.body
    .split(/\n{2,}/)
    .map(
      (para) =>
        `<p style="margin:0 0 16px;line-height:1.6;color:#1f2937;font-size:15px">${escapeHtml(para).replace(/\n/g, "<br />")}</p>`,
    )
    .join("");
  const signatureHtml = escapeHtml(p.signature).replace(/\n/g, "<br />");
  const cta =
    p.ctaUrl && p.ctaUrl !== "https://"
      ? `<table role="presentation" cellspacing="0" cellpadding="0" border="0" style="margin:24px 0"><tr><td style="border-radius:6px;background:${escapeHtml(p.accent)}"><a href="${escapeHtml(p.ctaUrl)}" style="display:inline-block;padding:12px 22px;font-family:Arial,sans-serif;font-size:14px;font-weight:600;color:#ffffff;text-decoration:none;border-radius:6px">${escapeHtml(p.ctaLabel)}</a></td></tr></table>`
      : "";

  return `<!doctype html>
<html lang="de">
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width,initial-scale=1" />
<meta name="color-scheme" content="light" />
<title>${escapeHtml(p.subject)}</title>
</head>
<body style="margin:0;padding:0;background:#f4f4f7;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Arial,sans-serif">
<div style="display:none;max-height:0;overflow:hidden;opacity:0;color:transparent">${escapeHtml(p.preheader)}</div>
<table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0" style="background:#f4f4f7">
  <tr><td align="center" style="padding:32px 12px">
    <table role="presentation" width="600" cellspacing="0" cellpadding="0" border="0" style="max-width:600px;width:100%;background:#ffffff;border-radius:8px;box-shadow:0 1px 3px rgba(0,0,0,.06)">
      <tr><td style="padding:32px 36px 8px"><div style="height:4px;width:48px;background:${escapeHtml(p.accent)};border-radius:2px"></div></td></tr>
      <tr><td style="padding:16px 36px 32px">
        <p style="margin:0 0 16px;line-height:1.6;color:#111827;font-size:15px">${escapeHtml(p.greeting)}</p>
        ${paragraphs}
        ${cta}
        <p style="margin:24px 0 0;line-height:1.6;color:#1f2937;font-size:15px">${signatureHtml}</p>
      </td></tr>
      <tr><td style="padding:20px 36px;border-top:1px solid #e5e7eb;background:#fafafa;border-radius:0 0 8px 8px">
        <p style="margin:0;font-size:12px;color:#6b7280;line-height:1.5">${escapeHtml(p.senderName)}</p>
      </td></tr>
    </table>
    <img src="${p.pixelUrl}" width="1" height="1" alt="" style="display:block;border:0;outline:none;width:1px;height:1px" />
  </td></tr>
</table>
</body>
</html>`;
}

/**
 * Prominenter "Test Track"-Button pro Kampagne.
 *
 * Feuert beide Tracking-Endpoints (klassischer .gif-Pixel und Stealth .png)
 * gegen die produktive Custom-Domain, zeigt den HTTP-Status pro Endpoint
 * und pollt anschliessend die DB (verifyPixelTestEvent) um zu bestaetigen,
 * dass das Event in der Queue oder bereits in tracking_events gelandet ist.
 */
function CampaignQuickTrackTest({ campaignId }: { campaignId: string }) {
  const aliasFn = useServerFn(getOrCreateCampaignAlias);
  const verifyFn = useServerFn(verifyPixelTestEvent);
  const hitFn = useServerFn(hitPixelTestEndpoints);
  const { data: alias } = useQuery({
    queryKey: ["pixel-alias", campaignId],
    queryFn: () => aliasFn({ data: { campaignId } }),
    staleTime: Infinity,
  });

  type Result = {
    gif: { status: number | null; ok: boolean; error?: string };
    png: { status: number | null; ok: boolean; error?: string };
    event: "queued" | "drained" | "pending" | "timeout" | "error";
    eventMessage?: string;
    requestId: string;
  };

  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<Result | null>(null);

  const ORIGIN = "https://meineasykredit.click";

  async function runTest() {
    setRunning(true);
    setResult(null);
    const requestId = `qtest-${crypto.randomUUID()}`;
    const rid = `qtr-${Math.random().toString(36).slice(2, 10)}`;

    // 1+2) Beide Pixel serverseitig aufrufen — kein CORS, kein Adblocker.
    let gif: Result["gif"] = { status: null, ok: false };
    let png: Result["png"] = { status: null, ok: false };
    try {
      const hit = await hitFn({
        data: {
          campaignId,
          requestId,
          origin: ORIGIN,
          shortToken: alias?.shortToken,
          recipientToken: rid,
        },
      });
      gif = hit.gif;
      png = hit.png;
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Server-Fehler";
      gif = { status: null, ok: false, error: msg };
      png = { status: null, ok: false, error: msg };
    }

    // 3) DB-Event verifizieren (poll bis 20s)
    let event: Result["event"] = "pending";
    let eventMessage: string | undefined;
    if (png.ok) {
      for (let attempt = 1; attempt <= 20; attempt++) {
        try {
          const v = await verifyFn({ data: { campaignId, requestId } });
          if (v.status === "drained") {
            event = "drained";
            break;
          }
          if (v.status === "queued") {
            event = "queued";
            break;
          }
        } catch (e) {
          event = "error";
          eventMessage = e instanceof Error ? e.message : "Verify-Fehler";
          break;
        }
        await new Promise((r) => setTimeout(r, 1000));
      }
      if (event === "pending") event = "timeout";
    } else {
      event = "error";
      eventMessage = "Pixel-Aufruf fehlgeschlagen — kein Event zu erwarten";
    }

    const finalResult: Result = { gif, png, event, eventMessage, requestId };
    setResult(finalResult);
    setRunning(false);

    const allGood = gif.ok && png.ok && (event === "queued" || event === "drained");
    if (allGood) {
      toast.success(
        event === "drained"
          ? "Test ok — Pixel 200 + Event in DB"
          : "Test ok — Pixel 200 + Event in Queue",
      );
    } else if (!gif.ok || !png.ok) {
      toast.error("Endpoint hat nicht 200 geliefert — siehe Details");
    } else {
      toast.error("Pixel ok, aber Event ist nicht in der DB angekommen");
    }
  }

  return (
    <div className="space-y-4 rounded-xl border border-primary/30 bg-primary/5 p-4 sm:p-5">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Zap className="size-4 text-primary" />
          <div>
            <Label className="text-sm font-semibold">Pixel-Schnelltest</Label>
            <p className="mt-1 text-xs text-muted-foreground">
              Prüft beide Pixel und die Speicherung.
            </p>
          </div>
        </div>
        <Button size="sm" onClick={() => void runTest()} disabled={running}>
          {running ? (
            <>
              <Loader2 className="size-4 animate-spin mr-1" />
              Teste…
            </>
          ) : (
            <>
              <PlayCircle className="size-4 mr-1" />
              Jetzt testen
            </>
          )}
        </Button>
      </div>
      {result && (
        <div className="space-y-2 pt-1 font-mono text-sm">
          <ResultRow
            label=".gif Pixel"
            ok={result.gif.ok}
            detail={
              result.gif.error
                ? result.gif.error
                : result.gif.status !== null
                  ? `HTTP ${result.gif.status}`
                  : "—"
            }
          />
          <ResultRow
            label=".png Stealth"
            ok={result.png.ok}
            detail={
              result.png.error
                ? result.png.error
                : result.png.status !== null
                  ? `HTTP ${result.png.status}`
                  : "—"
            }
          />
          <ResultRow
            label="DB Event"
            ok={result.event === "queued" || result.event === "drained"}
            detail={
              result.event === "drained"
                ? "in tracking_events (drained)"
                : result.event === "queued"
                  ? "in tracking_event_queue (wartet auf drain)"
                  : result.event === "timeout"
                    ? "Timeout nach 20s — kein Event gefunden"
                    : (result.eventMessage ?? "Fehler")
            }
          />
          <div className="break-all pt-1 text-xs text-muted-foreground">
            requestId: <span className="font-mono">{result.requestId}</span>
          </div>
        </div>
      )}
    </div>
  );
}

function ResultRow({ label, ok, detail }: { label: string; ok: boolean; detail: string }) {
  return (
    <div className="flex items-center gap-2">
      {ok ? (
        <CheckCircle2 className="size-3.5 text-green-500 shrink-0" />
      ) : (
        <AlertCircle className="size-3.5 text-destructive shrink-0" />
      )}
      <span className="text-muted-foreground w-20 shrink-0">{label}</span>
      <span className={ok ? "text-foreground" : "text-destructive"}>{detail}</span>
    </div>
  );
}

// ============================================================================
// CampaignE2EEmailTest
// Vollstaendiger End-to-End-Test: schickt ueber eine verbundene Gmail-OAuth-
// Seed-Inbox eine echte Mail an sich selbst, die das Tracking-Pixel der
// Kampagne enthaelt. Anschliessend pollen wir tracking_event_queue und
// tracking_events, bis das Open-Event tatsaechlich gespeichert wurde.
// ============================================================================

type E2ETestPhase =
  | { phase: "idle" }
  | { phase: "sending" }
  | {
      phase: "polling";
      attempt: number;
      sentAt: string;
      subject: string;
      tag: string;
    }
  | {
      phase: "ok";
      layer: "queue" | "events";
      provider: string | null;
      device: string | null;
      country: string | null;
      at: string;
      sentAt: string;
      latencyMs: number;
      tag: string;
    }
  | {
      phase: "timeout";
      sentAt: string;
      tag: string;
    }
  | { phase: "error"; message: string; needsReconnect?: boolean };

function CampaignE2EEmailTest({
  campaignId,
  campaignName,
}: {
  campaignId: string;
  campaignName: string;
}) {
  const sendFn = useServerFn(sendCampaignE2ETestEmail);
  const verifyFn = useServerFn(verifyCampaignE2EOpen);
  const listRecipientsFn = useServerFn(listCampaignTestRecipients);
  const previewFn = useServerFn(previewCampaignE2ETestEmail);
  const [state, setState] = useState<E2ETestPhase>({ phase: "idle" });
  const [selectedInboxId, setSelectedInboxId] = useState<string>("");
  const [selectedRecipientId, setSelectedRecipientId] = useState<string>("__random__");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [preview, setPreview] = useState<{
    recipientEmail: string;
    fields: Record<string, string>;
    subject: string;
    htmlBody: string;
    subjectsAvailable: number;
    hasRecipient: boolean;
  } | null>(null);

  // Verbundene Gmail-OAuth-Seed-Inboxes des Users laden (nur die koennen senden).
  const inboxes = useQuery({
    queryKey: ["e2e-gmail-inboxes"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("seed_inboxes")
        .select("id,email,display_name,auth_method,status")
        .eq("auth_method", "oauth_google")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
    staleTime: 60_000,
  });

  const recipientsQ = useQuery({
    queryKey: ["e2e-campaign-recipients", campaignId],
    queryFn: () => listRecipientsFn({ data: { campaignId } }),
    staleTime: 30_000,
  });

  useEffect(() => {
    if (!selectedInboxId && inboxes.data && inboxes.data.length > 0) {
      setSelectedInboxId(inboxes.data[0].id as string);
    }
  }, [inboxes.data, selectedInboxId]);

  async function runTest() {
    if (!selectedInboxId) {
      toast.error("Bitte eine Gmail-Seed-Inbox auswaehlen.");
      return;
    }
    setState({ phase: "sending" });
    let sent;
    try {
      sent = await sendFn({
        data: {
          campaignId,
          seedInboxId: selectedInboxId,
          recipientId:
            selectedRecipientId && selectedRecipientId !== "__random__"
              ? selectedRecipientId
              : undefined,
        },
      });
    } catch (e) {
      setState({
        phase: "error",
        message: `Sende-Fehler: ${e instanceof Error ? e.message : "Fehler"}`,
      });
      return;
    }
    if (!sent.ok) {
      setState({
        phase: "error",
        message: sent.error,
        needsReconnect: "needsReconnect" in sent ? sent.needsReconnect : undefined,
      });
      return;
    }
    toast.success("Test-Mail versendet — warte auf Gmail-Image-Proxy-Hit…");
    const sentAt = sent.sentAt;
    const sentMs = new Date(sentAt).getTime();
    const tag = sent.tag;
    const recipientLabel = sent.recipientLabel;
    const subject = sent.subject;

    // Poll: bis 90s, alle 3s. Gmail braucht typisch 5-30s bis der Image-
    // Proxy die Mail rendert und das Pixel abruft.
    const maxAttempts = 30;
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      setState({ phase: "polling", attempt, sentAt, subject, tag });
      try {
        const v = await verifyFn({ data: { campaignId, recipientLabel } });
        if (v.status === "drained" || v.status === "queued") {
          const atMs = new Date(v.at).getTime();
          setState({
            phase: "ok",
            layer: v.status === "drained" ? "events" : "queue",
            provider: v.provider ?? null,
            device: v.device ?? null,
            country: v.country ?? null,
            at: v.at,
            sentAt,
            latencyMs: Math.max(0, atMs - sentMs),
            tag,
          });
          toast.success(
            v.status === "drained"
              ? "Open-Event in tracking_events gespeichert!"
              : "Open-Event in Queue — wird gleich gedraint.",
          );
          return;
        }
      } catch (e) {
        setState({
          phase: "error",
          message: `Verifikation fehlgeschlagen: ${e instanceof Error ? e.message : "Fehler"}`,
        });
        return;
      }
      await new Promise((r) => setTimeout(r, 3000));
    }
    setState({ phase: "timeout", sentAt, tag });
    toast.error("90s gewartet — kein Open-Event. Pruefe Inbox/Spam, ob die Mail angekommen ist.");
  }

  async function openPreview() {
    setPreviewOpen(true);
    setPreviewLoading(true);
    setPreview(null);
    try {
      const p = await previewFn({
        data: {
          campaignId,
          recipientId:
            selectedRecipientId && selectedRecipientId !== "__random__"
              ? selectedRecipientId
              : undefined,
        },
      });
      setPreview(p);
    } catch (e) {
      toast.error(`Vorschau-Fehler: ${e instanceof Error ? e.message : "Fehler"}`);
      setPreviewOpen(false);
    } finally {
      setPreviewLoading(false);
    }
  }

  const noInboxes = !inboxes.isLoading && (inboxes.data?.length ?? 0) === 0;
  const busy = state.phase === "sending" || state.phase === "polling";

  return (
    <div className="space-y-4 rounded-xl border border-border/70 bg-muted/15 p-4 sm:p-5">
      <div className="flex items-center justify-between gap-2">
        <Label className="text-sm font-semibold">
          End-to-End: echte Mail senden &rarr; Open in DB pruefen
        </Label>
        {inboxes.isLoading && <span className="text-xs text-muted-foreground">lade Inboxes…</span>}
      </div>

      {noInboxes ? (
        <p className="text-sm leading-relaxed text-muted-foreground">
          Keine Gmail-OAuth-Seed-Inbox verbunden. Verbinde unter{" "}
          <Link to="/seed-inboxes" className="underline text-primary">
            Seed-Inboxes
          </Link>{" "}
          eine Gmail-Inbox (mit <code className="font-mono">gmail.send</code>-Scope), damit der
          E2E-Test eine echte Mail versenden kann.
        </p>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <Select
              value={selectedInboxId}
              onValueChange={setSelectedInboxId}
              disabled={busy || inboxes.isLoading}
            >
              <SelectTrigger className="h-8 text-xs flex-1 min-w-[200px]">
                <SelectValue placeholder="Gmail-Inbox waehlen…" />
              </SelectTrigger>
              <SelectContent>
                {(inboxes.data ?? []).map((ib) => (
                  <SelectItem key={ib.id as string} value={ib.id as string}>
                    {(ib.display_name as string | null) ?? (ib.email as string)} —{" "}
                    {ib.email as string}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={selectedRecipientId}
              onValueChange={setSelectedRecipientId}
              disabled={busy || recipientsQ.isLoading}
            >
              <SelectTrigger className="h-8 text-xs flex-1 min-w-[200px]">
                <SelectValue placeholder="Empfaenger waehlen…" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__random__">Zufaelliger Empfaenger</SelectItem>
                {(recipientsQ.data?.recipients ?? []).map((r) => (
                  <SelectItem key={r.id} value={r.id}>
                    {r.email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              size="sm"
              variant="outline"
              disabled={busy}
              onClick={() => void openPreview()}
              title="Rendert Subject + HTML mit den CSV-Daten des Empfaengers"
            >
              <Eye className="size-4" /> Vorschau
            </Button>
            <Button
              size="sm"
              variant="default"
              disabled={!selectedInboxId || busy}
              onClick={() => void runTest()}
              title={`Sendet eine echte Mail an sich selbst mit dem Pixel von "${campaignName}" und pollt tracking_events`}
            >
              {busy ? <Loader2 className="size-4 animate-spin" /> : <Send className="size-4" />}
              {state.phase === "sending"
                ? "sende…"
                : state.phase === "polling"
                  ? `warte ${state.attempt * 3}s…`
                  : "Echten E2E-Test starten"}
            </Button>
          </div>

          <E2EStatusBlock state={state} />
        </>
      )}

      <p className="text-[10px] text-muted-foreground leading-relaxed">
        Sendet eine echte Mail mit dem Tracking-Pixel an die ausgewaehlte Gmail-Inbox. Sobald Gmail
        die Mail rendert, ruft der Image-Proxy das Pixel ab und es muss ein Eintrag in{" "}
        <code className="font-mono">tracking_events</code> entstehen. Eindeutige Zuordnung ueber
        einen Marker im Pixel-URL (kollidiert nicht mit echten Opens).
      </p>

      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Platzhalter-Vorschau</DialogTitle>
          </DialogHeader>
          {previewLoading || !preview ? (
            <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
              <Loader2 className="size-4 animate-spin" /> Rendere Vorschau…
            </div>
          ) : (
            <div className="space-y-3">
              <div className="text-xs text-muted-foreground">
                Empfaenger-Sample: <code className="font-mono">{preview.recipientEmail}</code>
                {!preview.hasRecipient && (
                  <span className="ml-2 text-amber-600">
                    (keine Empfaenger in der Kampagne — Platzhalter bleiben leer)
                  </span>
                )}
              </div>
              <div className="rounded-md border border-border/70 bg-muted/30 p-2">
                <div className="text-[10px] uppercase text-muted-foreground mb-1">Subject</div>
                <div className="text-sm font-medium">{preview.subject}</div>
              </div>
              {Object.keys(preview.fields).length > 0 && (
                <details className="rounded-md border border-border/70 bg-muted/20 p-2">
                  <summary className="text-[10px] uppercase text-muted-foreground cursor-pointer">
                    Verfuegbare Platzhalter ({Object.keys(preview.fields).length})
                  </summary>
                  <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-1 text-xs">
                    {Object.entries(preview.fields).map(([k, v]) => (
                      <div key={k} className="font-mono truncate">
                        <span className="text-muted-foreground">{`{{${k}}}`}</span> = {v || "—"}
                      </div>
                    ))}
                  </div>
                </details>
              )}
              <div className="rounded-md border border-border/70 bg-background">
                <div className="px-2 py-1 text-[10px] uppercase text-muted-foreground border-b">
                  HTML-Body (gerendert)
                </div>
                <iframe
                  title="Mail-Vorschau"
                  srcDoc={preview.htmlBody}
                  sandbox=""
                  className="w-full h-[500px] rounded-b-md bg-white"
                />
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function E2EStatusBlock({ state }: { state: E2ETestPhase }) {
  if (state.phase === "idle") return null;

  if (state.phase === "sending") {
    return (
      <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
        <Loader2 className="size-3 animate-spin" /> Sende Test-Mail via Gmail-API…
      </div>
    );
  }

  if (state.phase === "polling") {
    const waited = state.attempt * 3;
    return (
      <div className="space-y-1 text-[11px]">
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="size-3 animate-spin" />
          Mail versendet. Polle Open-Event (bisher {waited}s, max 90s)…
        </div>
        <div className="text-muted-foreground">
          Marker: <code className="font-mono">{state.tag}</code>
        </div>
      </div>
    );
  }

  if (state.phase === "ok") {
    return (
      <div className="space-y-1 rounded-md border border-green-600/40 bg-green-600/5 p-2 text-[11px]">
        <div className="flex items-center gap-2 font-medium text-green-700 dark:text-green-400">
          <CheckCircle2 className="size-4" />
          Open-Event gefunden in{" "}
          <code className="font-mono">
            {state.layer === "events" ? "tracking_events" : "tracking_event_queue"}
          </code>
        </div>
        <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-muted-foreground">
          <div>
            Latenz:{" "}
            <span className="text-foreground">{Math.round(state.latencyMs / 100) / 10}s</span>
          </div>
          <div>
            Provider: <span className="text-foreground">{state.provider ?? "—"}</span>
          </div>
          <div>
            Device: <span className="text-foreground">{state.device ?? "—"}</span>
          </div>
          <div>
            Land: <span className="text-foreground">{state.country ?? "—"}</span>
          </div>
        </div>
        <div className="text-muted-foreground">
          Marker: <code className="font-mono">{state.tag}</code>
        </div>
      </div>
    );
  }

  if (state.phase === "timeout") {
    return (
      <div className="space-y-1 rounded-md border border-amber-600/40 bg-amber-600/5 p-2 text-[11px]">
        <div className="flex items-center gap-2 font-medium text-amber-700 dark:text-amber-400">
          <Clock3 className="size-4" />
          Timeout — 90s gewartet, kein Event gefunden
        </div>
        <div className="text-muted-foreground">
          Moegliche Ursachen: Mail im Spam-Ordner gelandet (Gmail laedt dann keine Bilder),
          Image-Proxy noch nicht ausgeloest (manche Clients laden Bilder erst beim Oeffnen), oder
          die Mail wurde abgelehnt. Marker: <code className="font-mono">{state.tag}</code>
        </div>
      </div>
    );
  }

  // error
  return (
    <div className="space-y-1 rounded-md border border-destructive/40 bg-destructive/5 p-2 text-[11px]">
      <div className="flex items-center gap-2 font-medium text-destructive">
        <AlertCircle className="size-4" /> Fehler
      </div>
      <div className="text-muted-foreground">{state.message}</div>
      {state.needsReconnect && (
        <Link to="/seed-inboxes" className="text-[10px] underline text-primary">
          Inbox neu verbinden &rarr;
        </Link>
      )}
    </div>
  );
}

function SnippetValidationPanel({ result }: { result: SnippetValidationResult }) {
  const { warnings, score } = result;
  const errors = warnings.filter((w) => w.level === "error").length;
  const warns = warnings.filter((w) => w.level === "warn").length;
  const tone =
    errors > 0
      ? "border-destructive/50 bg-destructive/5"
      : warns > 0
        ? "border-amber-500/50 bg-amber-500/5"
        : "border-emerald-500/50 bg-emerald-500/5";
  const label =
    errors > 0
      ? `${errors} Fehler, ${warns} Warnung(en)`
      : warns > 0
        ? `${warns} Warnung(en)`
        : "Snippet ist sauber";
  return (
    <div className={`space-y-1 rounded-md border p-2 ${tone}`}>
      <div className="flex items-center justify-between gap-2">
        <Label className="text-[10px] text-muted-foreground">
          Snippet-Validierung (Spam-/Tracking-Heuristik)
        </Label>
        <span className="text-[10px] text-muted-foreground">
          {label} · Score {score}
        </span>
      </div>
      {warnings.length === 0 ? (
        <p className="text-[10px] text-emerald-700 dark:text-emerald-400">
          Keine bekannten Spam-/Tracking-Signaturen erkannt.
        </p>
      ) : (
        <ul className="space-y-0.5">
          {warnings.map((w) => (
            <li key={w.id} className="text-[10px] leading-snug">
              <span
                className={
                  w.level === "error"
                    ? "text-destructive font-medium"
                    : w.level === "warn"
                      ? "text-amber-700 dark:text-amber-400 font-medium"
                      : "text-muted-foreground"
                }
              >
                [{w.level}]
              </span>{" "}
              {w.message}
              {w.hint && <span className="text-muted-foreground"> — {w.hint}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// --- A/B Snippet-Varianten -----------------------------------------------

const STEALTH_STYLE_LABELS: Record<StealthStyle, string> = {
  "spacer-div": "Footer-Brand (Trennlinie + 'Gesendet mit'-Icon)",
  "footer-divider": "Footer-Divider (sichtbare Hairline, Icon als Bullet)",
  "signature-logo": "Signatur-Avatar (24x24, alt='Logo')",
  "inline-pixel": "Nacktes 1x1-Pixel (Kontroll-Variante)",
};

function buildVariantImg(style: StealthStyle, url: string): string {
  switch (style) {
    case "footer-divider":
      // Sichtbare Trennlinie + Mini-Bullet-Icon — wirkt wie ein typischer
      // Newsletter-Footer-Separator, kein Tracker.
      return (
        `<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" ` +
        `style="margin-top:16px;border-collapse:collapse">` +
        `<tr><td style="border-top:1px solid #e5e7eb;padding-top:8px;text-align:center;` +
        `font:11px/14px Arial,Helvetica,sans-serif;color:#9ca3af">` +
        `<img src="${url}" alt="" width="6" height="6" ` +
        `style="vertical-align:middle;border:0;border-radius:50%;opacity:.6" />` +
        `</td></tr></table>`
      );
    case "signature-logo":
      // Wirkt wie Signatur-Avatar; alt-Text bewusst sinnvoll.
      return (
        `<img src="${url}" alt="Logo" width="24" height="24" ` +
        `style="display:inline-block;border:0;border-radius:50%;vertical-align:middle" />`
      );
    case "inline-pixel":
      // Nackt — als Kontroll-Variante zum Vergleich (wird sofort als Tracker
      // erkannt; nur zum Benchmark der Tarnung).
      return `<img src="${url}" width="1" height="1" alt="" style="display:block;border:0;outline:none" />`;
    case "spacer-div":
    default:
      // Footer-Brand-Tagline: 14x14-Icon + "Gesendet mit"-Text in einer
      // Hairline-Tabelle. Sieht wie der Footer jedes Mailtools aus.
      return (
        `<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" ` +
        `style="margin-top:20px;border-collapse:collapse">` +
        `<tr><td style="border-top:1px solid #e5e7eb;padding:10px 0 0;text-align:center;` +
        `font:11px/14px Arial,Helvetica,sans-serif;color:#9ca3af">` +
        `<img src="${url}" alt="" width="14" height="14" ` +
        `style="vertical-align:middle;border:0;border-radius:2px;opacity:.7" />` +
        `<span style="vertical-align:middle;margin-left:6px">Gesendet mit easymail</span>` +
        `</td></tr></table>`
      );
  }
}

function SnippetABPanel({ campaignId }: { campaignId: string }) {
  const origin = "https://meineasykredit.click";
  const qc = useQueryClient();
  const listFn = useServerFn(listSnippetVariants);
  const createFn = useServerFn(createSnippetVariant);
  const deleteFn = useServerFn(deleteSnippetVariant);
  const statsFn = useServerFn(getVariantStats);

  const variantsQ = useQuery({
    queryKey: ["snippet-variants", campaignId],
    queryFn: () => listFn({ data: { campaignId } }),
  });
  const statsQ = useQuery({
    queryKey: ["snippet-variant-stats", campaignId],
    queryFn: () => statsFn({ data: { campaignId } }),
    refetchInterval: 15000,
  });

  const [newLabel, setNewLabel] = useState("");
  const [newStyle, setNewStyle] = useState<StealthStyle>("spacer-div");

  const variants = variantsQ.data?.variants ?? [];

  function buildUrl(token: string): string {
    return `${origin}/api/public/i/${token}.png?s={{sender_email}}&r={{email}}&m={{lead_id}}`;
  }

  const spintax = useMemo(() => {
    if (variants.length < 2) return "";
    const snippets = variants.map((v) => buildVariantImg(v.stealth_style, buildUrl(v.short_token)));
    return `{${snippets.join("|")}}`;
  }, [variants]);

  async function onCreate() {
    const label = newLabel.trim() || String.fromCharCode(65 + variants.length); // A, B, C ...
    try {
      await createFn({ data: { campaignId, label, stealthStyle: newStyle } });
      setNewLabel("");
      void qc.invalidateQueries({ queryKey: ["snippet-variants", campaignId] });
      void qc.invalidateQueries({ queryKey: ["snippet-variant-stats", campaignId] });
      toast.success(`Variante ${label} angelegt`);
    } catch (e) {
      toast.error(`Anlegen fehlgeschlagen: ${e instanceof Error ? e.message : "Fehler"}`);
    }
  }
  async function onDelete(variantId: string, label: string) {
    if (!confirm(`Variante ${label} wirklich loeschen?`)) return;
    try {
      await deleteFn({ data: { campaignId, variantId } });
      void qc.invalidateQueries({ queryKey: ["snippet-variants", campaignId] });
      void qc.invalidateQueries({ queryKey: ["snippet-variant-stats", campaignId] });
      toast.success("Variante geloescht");
    } catch (e) {
      toast.error(`Loeschen fehlgeschlagen: ${e instanceof Error ? e.message : "Fehler"}`);
    }
  }

  const stats = statsQ.data?.variants ?? [];
  const statsById = new Map(stats.map((s) => [s.id, s]));
  const totalUnique = stats.reduce((acc, s) => acc + s.uniqueRecipients, 0);

  return (
    <div className="space-y-2 rounded-md border border-border/60 bg-muted/10 p-3">
      <div className="flex items-center justify-between">
        <Label className="text-xs text-muted-foreground">
          A/B-Snippet-Varianten (Plusvibe-Spintax)
        </Label>
        <span className="text-[10px] text-muted-foreground">
          {variants.length} Variante{variants.length === 1 ? "" : "n"}
        </span>
      </div>

      {/* Variant-Liste */}
      <div className="space-y-1.5">
        {variants.length === 0 && (
          <p className="text-[10px] text-muted-foreground">
            Noch keine Varianten. Mindestens 2 anlegen, dann unten den Spintax-Block in Plusvibe
            einsetzen. Plusvibe waehlt pro Empfaenger zufaellig genau eine.
          </p>
        )}
        {variants.map((v) => {
          const s = statsById.get(v.id);
          const url = buildUrl(v.short_token);
          const snippet = buildVariantImg(v.stealth_style, url);
          return (
            <div
              key={v.id}
              className="rounded-md border border-border/50 bg-background/50 p-2 space-y-1"
            >
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-xs font-semibold">{v.label}</span>
                  <span className="text-[10px] text-muted-foreground truncate">
                    {STEALTH_STYLE_LABELS[v.stealth_style] ?? v.stealth_style}
                  </span>
                  <code className="text-[10px] font-mono text-muted-foreground">
                    {v.short_token}
                  </code>
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-6 w-6"
                    title="Snippet kopieren"
                    onClick={() => {
                      void navigator.clipboard.writeText(snippet);
                      toast.success(`Variante ${v.label} kopiert`);
                    }}
                  >
                    <Code2 className="size-3.5" />
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-6 w-6 text-destructive"
                    title="Variante loeschen"
                    onClick={() => void onDelete(v.id, v.label)}
                  >
                    <Trash2 className="size-3.5" />
                  </Button>
                </div>
              </div>
              {s && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[10px]">
                  <VariantMetric label="Hits" value={String(s.hits)} />
                  <VariantMetric
                    label="Unique Recipients (~Sent)"
                    value={String(s.uniqueRecipients)}
                  />
                  <VariantMetric
                    label="Open-Delay p50"
                    value={s.openDelaySecondsMedian === null ? "—" : `${s.openDelaySecondsMedian}s`}
                  />
                  <VariantMetric
                    label="Open-Delay p95"
                    value={s.openDelaySecondsP95 === null ? "—" : `${s.openDelaySecondsP95}s`}
                  />
                </div>
              )}
              {s && Object.keys(s.providers).length > 0 && (
                <div className="text-[10px] text-muted-foreground">
                  Provider:{" "}
                  {Object.entries(s.providers)
                    .map(([k, n]) => `${k} ${n}`)
                    .join(" · ")}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Neue Variante */}
      <div className="flex flex-wrap items-center gap-2 pt-1">
        <Input
          value={newLabel}
          onChange={(e) => setNewLabel(e.target.value)}
          placeholder={`Label (z.B. ${String.fromCharCode(65 + variants.length)})`}
          className="h-7 text-xs w-24"
        />
        <Select value={newStyle} onValueChange={(v) => setNewStyle(v as StealthStyle)}>
          <SelectTrigger className="h-7 text-xs w-[260px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {STEALTH_STYLES.map((s) => (
              <SelectItem key={s} value={s} className="text-xs">
                {STEALTH_STYLE_LABELS[s]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button size="sm" variant="outline" onClick={() => void onCreate()}>
          <Plus className="size-3.5" /> Variante anlegen
        </Button>
      </div>

      {/* Spintax-Block */}
      {variants.length >= 2 && (
        <div className="space-y-1 rounded-md border border-dashed border-primary/40 bg-primary/5 p-2">
          <div className="flex items-center justify-between">
            <Label className="text-[10px] text-muted-foreground">
              Plusvibe-Spintax (1:1 in den HTML-Editor einsetzen)
            </Label>
            <Button
              size="icon"
              variant="default"
              className="h-6 w-6"
              title="Spintax kopieren"
              onClick={() => {
                void navigator.clipboard.writeText(spintax);
                toast.success(
                  "Spintax kopiert — Plusvibe waehlt pro Empfaenger zufaellig 1 Variante",
                );
              }}
            >
              <Copy className="size-3.5" />
            </Button>
          </div>
          <code className="block text-[10px] font-mono break-all text-foreground/80 max-h-32 overflow-auto">
            {spintax}
          </code>
        </div>
      )}

      {/* Hinweise / Vergleichs-Hilfe */}
      <div className="text-[10px] text-muted-foreground leading-relaxed space-y-1 pt-1">
        <p>
          <strong>Vergleichsbasis:</strong> Sent pro Variante wird aus eindeutigen Pixel-Hits
          (unique recipients) hochgerechnet — Untergrenze, da Empfaenger ohne jeglichen Bild-Load
          nicht zaehlen. Total: {totalUnique} unique Hits ueber alle Varianten.
        </p>
        <p>
          <strong>Bounces / Spam-Platzierung:</strong> Bounces ({statsQ.data?.totalBounces ?? 0})
          und Seed-Platzierung sind aktuell auf Kampagnen-Ebene und lassen sich nicht pro Variante
          aufschluesseln, weil Plusvibe-Bounce-Webhook und Seed-Scanner kein Variant-Tag mitliefern.
        </p>
        {statsQ.data?.orphan && (
          <p className="text-amber-600 dark:text-amber-400">
            {statsQ.data.orphan.hits} Hits ohne Variant-Tag — vermutlich aus der urspruenglichen
            Kampagnen-URL (vor A/B-Setup).
          </p>
        )}
      </div>
    </div>
  );
}

function VariantMetric({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded border border-border/40 bg-background/60 px-2 py-1">
      <div className="text-[9px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="text-xs font-semibold">{value}</div>
    </div>
  );
}

function LiveSendStatus({
  campaign,
  onChanged,
}: {
  campaign: {
    id: string;
    status: string;
    live_phase?: string | null;
    live_phase_until?: string | null;
    send_pause_seconds?: number | null;
    live_last_activity_at?: string | null;
  };
  onChanged: () => void;
}) {
  const [pauseInput, setPauseInput] = useState<string>(String(campaign.send_pause_seconds ?? 30));
  const [saving, setSaving] = useState(false);
  // re-render every second für Countdown
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, []);

  const isRunning = campaign.status === "running";
  const phase = campaign.live_phase ?? "idle";
  const untilMs = campaign.live_phase_until ? new Date(campaign.live_phase_until).getTime() : 0;
  const remainingSec = Math.max(0, Math.ceil((untilMs - Date.now()) / 1000));

  let label = "Inaktiv";
  let dotClass = "bg-muted-foreground/40";
  if (isRunning) {
    if (phase === "pausing" && remainingSec > 0) {
      label = `Pause: ${remainingSec}s bis nächster Versand`;
      dotClass = "bg-amber-500 animate-pulse";
    } else if (phase === "sending") {
      label = "Sendet gerade …";
      dotClass = "bg-emerald-500 animate-pulse";
    } else {
      label = "Wartet auf nächsten Versand-Tick …";
      dotClass = "bg-sky-500 animate-pulse";
    }
  } else if (campaign.status === "paused") {
    label = "Pausiert";
    dotClass = "bg-amber-500";
  } else if (campaign.status === "stopped") {
    label = "Gestoppt";
    dotClass = "bg-destructive";
  } else if (campaign.status === "completed") {
    label = "Abgeschlossen";
    dotClass = "bg-emerald-500";
  }

  const savePause = async () => {
    const v = Math.max(0, Math.min(3600, Math.floor(Number(pauseInput) || 0)));
    setSaving(true);
    const { error } = await supabase
      .from("campaigns")
      .update({ send_pause_seconds: v })
      .eq("id", campaign.id);
    setSaving(false);
    if (error) {
      toast.error("Pause speichern fehlgeschlagen: " + error.message);
      return;
    }
    setPauseInput(String(v));
    toast.success(`Pause auf ${v}s gesetzt`);
    onChanged();
  };

  return (
    <div className="mt-3 flex flex-wrap items-center justify-between gap-3 rounded-md border border-border/60 bg-background/40 px-3 py-2">
      <div className="flex items-center gap-2 text-xs">
        <span className={`inline-block size-2 rounded-full ${dotClass}`} />
        <span className="font-medium">{label}</span>
        {campaign.live_last_activity_at && (
          <span className="text-muted-foreground">
            · letzte Aktivität {new Date(campaign.live_last_activity_at).toLocaleTimeString()}
          </span>
        )}
      </div>
      <div className="flex items-center gap-2 text-xs">
        <Label htmlFor={`pause-${campaign.id}`} className="text-muted-foreground">
          Pause zwischen Sends (s)
        </Label>
        <Input
          id={`pause-${campaign.id}`}
          type="number"
          min={0}
          max={3600}
          value={pauseInput}
          onChange={(e) => setPauseInput(e.target.value)}
          className="h-7 w-20 text-xs"
        />
        <Button
          size="sm"
          variant="outline"
          className="h-7 text-xs"
          disabled={saving || pauseInput === String(campaign.send_pause_seconds ?? 30)}
          onClick={savePause}
        >
          Speichern
        </Button>
      </div>
    </div>
  );
}
